import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import ClientChat from './ClientChat';
import PaymentPage from './PaymentPage';
import OnboardingForm from './OnboardingForm';
import RegisterPhone from './RegisterPhone';
import OtpVerify from './OtpVerify';
import Settings from './Settings';
import DashboardHome from '../components/DashboardHome';
import Campaigns from './Campaigns';
import CreateCampaigns from './CreateCampaigns';
import Contacts from './Contacts';
import ChatbotFlow from './ChatbotFlow';
import FlowEditor from './FlowEditor';
import { getProvisioningStatus, exportConversations } from '../api/whatsapp';

const ClientDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [account, setAccount] = useState(null);
  const [view, setView] = useState(searchParams.get('view') || 'dashboard');
  const [campaignView, setCampaignView] = useState(searchParams.get('campaign') || 'list');
  const [flowView, setFlowView] = useState(searchParams.get('flow') || 'list');
  const [editFlowId, setEditFlowId] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getProvisioningStatus();
      setStatus(data.status);
      setAccount(data.account);
    } catch { setStatus('error'); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (status !== 'active') return;
    const t = setInterval(fetchStatus, 60000);
    return () => clearInterval(t);
  }, [status, fetchStatus]);

  useEffect(() => {
    const params = {};
    if (view !== 'dashboard') params.view = view;
    if (campaignView !== 'list') params.campaign = campaignView;
    if (flowView !== 'list') params.flow = flowView;
    const current = Object.fromEntries(searchParams.entries());
    const changed = Object.keys(params).length !== Object.keys(current).length ||
      Object.keys(params).some(k => params[k] !== current[k]);
    if (changed) setSearchParams(params, { replace: true });
  }, [view, campaignView, flowView, searchParams, setSearchParams]);

  if (status === 'loading') {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 8 }}><CircularProgress /></Box>;
  }

  const handleNavigate = (key) => {
    if (key === 'dashboard' || (status === 'active' || status === 'otp_verified')) {
      setView(key);
    }
  };

  const renderContent = () => {
    if (status === 'pending') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f8faff', gap: 2 }}>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#075e54' }}>Account Pending Approval</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 460 }}>
              Your account is under review. Admin will approve it shortly.
            </Typography>
          </Box>
        </Box>
      );
    }

    if (status === 'payment_pending') {
      if (view !== 'dashboard') return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f8faff', gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#075e54' }}>Payment Required</Typography>
          <Typography variant="body2" color="text.secondary">Please complete payment to access this section.</Typography>
          <Button variant="contained" onClick={() => setView('dashboard')}
            sx={{ borderRadius: 2, textTransform: 'none', background: 'linear-gradient(135deg, #075e54, #0a8a7a)', px: 4 }}>
            Go to Payment
          </Button>
        </Box>
      );
      return <PaymentPage onComplete={() => { setStatus('onboarding'); fetchStatus(); }} />;
    }

    if (status === 'onboarding') {
      return <OnboardingForm account={account} onComplete={() => { setStatus('details_submitted'); fetchStatus(); }} />;
    }

    if (status === 'details_submitted') {
      return <RegisterPhone account={account} onComplete={() => { setStatus('otp_ready'); fetchStatus(); }} />;
    }

    if (status === 'otp_ready') {
      return <OtpVerify account={account} otpAlreadySent={true}
        onComplete={() => { setStatus('active'); setView('dashboard'); fetchStatus(); }}
        onChangeNumber={() => fetchStatus()} />;
    }

    if (status === 'otp_verified' || status === 'active') {
      if (view === 'chats') return <ClientChat />;
      if (view === 'chatbotflow') {
        if (flowView === 'edit' && editFlowId) return <FlowEditor id={editFlowId} onBack={() => setFlowView('list')} />;
        if (flowView === 'new') return <FlowEditor onBack={() => setFlowView('list')} />;
        return (
          <ChatbotFlow
            onCreateNew={() => setFlowView('new')}
            onEdit={(id) => { setEditFlowId(id); setFlowView('edit'); }}
          />
        );
      }
      if (view === 'settings') return <Settings account={account} onUpdated={fetchStatus} />;
      if (view === 'export') return <ExportView />;
      if (view === 'contacts') return <Contacts />;
      if (view === 'dashboard') return <DashboardHome account={account} onNavigateCampaigns={() => setView('campaigns')} />;
      if (view === 'campaigns') {
        if (campaignView === 'create') return <CreateCampaigns account={account} onBack={() => setCampaignView('list')} onCreated={() => { setCampaignView('list'); fetchStatus(); }} />;
        return <Campaigns onNavigateCreate={() => setCampaignView('create')} />;
      }
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f8faff', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#075e54' }}>Dashboard</Typography>
          <Typography variant="body1" color="text.secondary">Select a section from the sidebar.</Typography>
        </Box>
      );
    }

    if (status === 'suspended') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f8faff', gap: 2 }}>
          <Box sx={{ bgcolor: '#fee2e2', borderRadius: '50%', width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#ef4444', animation: 'pulse 1s infinite' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444' }}>Account Suspended</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 420, textAlign: 'center' }}>
            Your account is suspended. Contact admin.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, bgcolor: '#f8faff', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Unknown Status</Typography>
        <Typography variant="body1" color="text.secondary">Status: {status}. Contact support.</Typography>
      </Box>
    );
  };

  return (
    <Layout activeView={view} onNavigate={handleNavigate} status={status} apiStatus={account?.apiStatus} apiStatusDetail={account?.apiStatusDetail}>
      {renderContent()}
    </Layout>
  );
};

const ExportView = () => {
  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportConversations();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'conversations.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export failed'); }
    setExporting(false);
  };
  return (
    <Box sx={{ p: 4, maxWidth: 600 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Export Data</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Download your conversations as CSV file.</Typography>
      <Button variant="contained" onClick={handleExport} disabled={exporting}
        sx={{ borderRadius: 2, textTransform: 'none', background: 'linear-gradient(135deg, #075e54, #0a8a7a)', px: 4 }}>
        {exporting ? 'Exporting...' : 'Export Conversations (CSV)'}
      </Button>
    </Box>
  );
};

export default ClientDashboard;
